import { observer } from 'mobx-react-lite';
import React, { memo, useState } from 'react';
import { FormattedDate, FormattedNumber } from 'react-intl';
import { Link, useHistory } from 'react-router-dom';
import Box from 'ui-box';
import {
  Button,
  Container,
  Heading,
  majorScale,
  PageContainer,
  PageLoader,
  Spinner,
  VirtualTable,
  VirtualTableItem,
} from '../../npaper';
import { FilterPopover } from '../../npaper/FilterPopover';
import { Agreement, IAgreement, useStores } from '../../stores';
import { useFilterFetch } from '../../stores/utils/useFilterFetch';
import { CompanyThumbnail } from '../companies';
import { AgreementDetailsDialog } from './AgreementDetailsDialog';
import { AgreementStatusTag } from './AgreementStatusTag';

export const Agreements = () => {
  const { agreementStore } = useStores();
  const history = useHistory();
  const { items, isLoading, fetchMore, total, setFilter, filter } =
    useFilterFetch<typeof Agreement>(agreementStore as any);
  const [isCreatingAgreement, setIsCreatingAgreement] = useState(false);

  return !items ? (
    <PageContainer>
      <PageLoader />
    </PageContainer>
  ) : (
    <PageContainer
      display="flex"
      flexDirection="column"
      paddingBottom="0"
      height="100%"
      maxHeight="100vh"
    >
      <AgreementDetailsDialog
        isShown={isCreatingAgreement}
        onClose={(agreement) => {
          setIsCreatingAgreement(false);
          if (agreement) {
            history.push(`/agreements/${agreement.id}`);
          }
        }}
      />
      <Container marginBottom={majorScale(2)} flexShrink={0} flexGrow={0}>
        <Heading size={700} marginBottom={majorScale(3)}>
          Løbende aftaler
        </Heading>

        <Box display="flex" justifyContent="space-between">
          <FilterPopover<any>
            filter={filter.filter}
            onChange={(filter) => setFilter(filter)}
            filterFields={[]}
          />

          <Button iconLeft="add" onClick={() => setIsCreatingAgreement(true)}>
            Opret løbende aftale
          </Button>
        </Box>
      </Container>

      <VirtualTable
        isLoading={isLoading}
        items={items}
        total={total}
        rowHeight={48}
        header={[
          { title: 'Aftale', width: 120 },
          { title: 'Kunde' },
          { title: 'Status', width: 140 },
          { title: 'Titel' },
          { title: 'Oprettet', width: 180 },
          { title: 'Total', align: 'right', width: 180 },
          { title: 'Interval' },
          { title: 'Næste faktura', width: 180 },
          { title: 'Ansvarlig', width: 180 },
        ]}
        onRequestMore={async () => fetchMore()}
        renderRow={({ item: agreement, index }) => (
          <MemoizedRow agreement={agreement} />
        )}
      />
    </PageContainer>
  );
};

const MemoizedRow = memo(
  observer(({ agreement }: { agreement: IAgreement }) => (
    <>
      <VirtualTableItem>
        <Link to={`/agreements/${agreement.id}`} className="font-mono medium a">
          {agreement.id}
        </Link>
      </VirtualTableItem>
      <VirtualTableItem>
        {agreement.customer ? (
          <CompanyThumbnail company={agreement.customer} />
        ) : (
          <Spinner size={16} />
        )}
      </VirtualTableItem>
      <VirtualTableItem>
        <AgreementStatusTag agreement={agreement} small />
      </VirtualTableItem>
      <VirtualTableItem>{agreement.title}</VirtualTableItem>
      <VirtualTableItem>
        <FormattedDate value={agreement.created_at} format="default" />
      </VirtualTableItem>
      <VirtualTableItem justifyContent="flex-end" className="font-mono">
        <FormattedNumber
          format="currency"
          currency={agreement.currency}
          value={agreement.price}
        />
      </VirtualTableItem>
      <VirtualTableItem>{agreement.intervalTitle}</VirtualTableItem>
      <VirtualTableItem>
        <FormattedDate value={agreement.nextCycleDate} format="default" />
      </VirtualTableItem>
      <VirtualTableItem>
        {agreement.responsible.length > 0 ? (
          agreement.responsible.reduce((p, n) => !!p && !!n, true) ? (
            agreement.responsible.map((member) => member!.user.name).join(', ')
          ) : (
            ''
          )
        ) : (
          <span className="muted">Ikke angivet</span>
        )}
      </VirtualTableItem>
    </>
  ))
);
